// A multi-select Business Unit picker. Value is an array of businessUnitIds;
// what an EMPTY array means depends on the caller — "all business units"
// (scoping arrays, the default label) or "unassigned" (a job type's roll-up
// set) — so the placeholder is a prop. Zero-cost when unused: renders nothing
// when the tenant has no active business units.
//
// An assigned unit that has since been ARCHIVED is shown, marked, and
// removable, but never offered for selection: it is listed only while it is
// already in `value`, in its own group, where picking it takes it off. See
// useBusinessUnitOptions for why resolving the selection against the active
// list alone left records permanently unsavable.
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/atoms/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { cn } from '@/lib/utils';
import { resolveSelectedBusinessUnits, useBusinessUnitOptions } from './useBusinessUnitOptions';

export interface BusinessUnitMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /** Placeholder for an empty selection. Defaults to "All business units". */
  emptyLabel?: string;
}

export function BusinessUnitMultiSelect({
  value,
  onChange,
  disabled,
  emptyLabel = 'All business units',
  ariaLabel,
}: BusinessUnitMultiSelectProps) {
  const { activeUnits, unitsById } = useBusinessUnitOptions();

  // Zero-cost when unused — hide entirely until the tenant defines a unit.
  // Keyed on ACTIVE units: an archived-only tenant still sees nothing.
  if (activeUnits.length === 0) return null;

  // Render in SELECTION order (not catalog order): for a job type the order
  // is the preference order and the first unit is the resolver's fallback.
  const selected = resolveSelectedBusinessUnits(value, unitsById);
  const archivedSelected = selected.filter((entry) => entry.archived);

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label={ariaLabel}
          type="button"
          variant="outline"
          disabled={disabled}
          className="h-auto min-h-9 w-full items-start justify-between py-1.5 font-normal whitespace-normal"
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-left">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">{emptyLabel}</span>
            ) : (
              selected.map((entry) => (
                <Badge
                  key={entry.id}
                  variant={entry.archived ? 'outline' : 'secondary'}
                  className={cn('gap-1', entry.archived && 'border-dashed text-muted-foreground')}
                >
                  {entry.unit ? (
                    <span
                      className="h-2 w-2 rounded-full border"
                      style={{ backgroundColor: entry.unit.color }}
                    />
                  ) : null}
                  {entry.label}
                  {entry.note ? (
                    <span className="text-[10px] font-semibold tracking-wide uppercase">
                      {entry.note}
                    </span>
                  ) : null}
                </Badge>
              ))
            )}
          </span>
          <ChevronsUpDown className="mt-0.5 ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search business units…" />
          <CommandList>
            <CommandEmpty>No business units found.</CommandEmpty>
            <CommandGroup>
              {activeUnits.map((unit) => {
                const checked = value.includes(unit.id);
                return (
                  <CommandItem key={unit.id} value={unit.name} onSelect={() => toggle(unit.id)}>
                    <Check className={cn('mr-2 size-4', checked ? 'opacity-100' : 'opacity-0')} />
                    <span
                      className="mr-2 h-2.5 w-2.5 rounded-full border"
                      style={{ backgroundColor: unit.color }}
                    />
                    <span className="truncate">{unit.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {archivedSelected.length > 0 ? (
              // Populated ONLY from `value`, so removing the last entry takes
              // the group with it — there is no path back in.
              <CommandGroup heading="Assigned but no longer available">
                {archivedSelected.map((entry) => (
                  <CommandItem
                    key={entry.id}
                    value={`${entry.label} ${entry.note}`}
                    onSelect={() => toggle(entry.id)}
                  >
                    <Check className="mr-2 size-4" />
                    <span className="truncate">{entry.label}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {entry.note} — select to remove
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
